import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { newAxiosClient } from "@shadowcue/ui";
import type { IUser } from "../../../types/iuser";
import {
  PageContainer,
  PageTopbar,
  PageHeader,
  ContentContainer,
  DataTable,
  BreadcrumbItem,
  astryx,
  neutralTheme,
  Plus,
  Search,
  Contact,
  useDocumentTheme,
  defaultPagination,
  type IPagination,
} from "@shadowcue/ui";

interface ContactsIndexProps {
  currentUser: IUser;
}

// Declared as a type alias (not an interface) so it satisfies Astryx
// Table's `T extends Record<string, unknown>` generic constraint — plain
// interfaces don't structurally match an index-signature constraint.
type IContactRecord = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  status: string;
};

const axios = newAxiosClient();

const DEBOUNCE_DURATION = 500;

const ContactsIndex = (props: ContactsIndexProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [contacts, setContacts] = useState<IContactRecord[]>([]);
  const [pagination, setPagination] =
    useState<IPagination>(defaultPagination());
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>(searchParams.get("search") ?? "");
  const [debouncedSearch, setDebouncedSearch] = useState<string>(
    searchParams.get("search") ?? "",
  );

  const navigate = useNavigate();
  const mode = useDocumentTheme();

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Contacts", href: "/admin/contacts" },
  ];

  const fetchContacts = async (page: number = pagination.page) => {
    setLoading(true);
    try {
      const res = await axios.get("/api/contacts", {
        headers: {
          AUTHORIZATION: `Bearer ${props.currentUser.authToken}`,
        },
        params: {
          page,
          limit: pagination.limit,
          search: debouncedSearch || undefined,
        },
      });
      setContacts(res.data.contacts || []);
      setPagination(res.data.pagination || defaultPagination());
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(
      () => setDebouncedSearch(search),
      DEBOUNCE_DURATION,
    );
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    const nextParams = new URLSearchParams(searchParams);
    if (debouncedSearch.trim()) {
      nextParams.set("search", debouncedSearch.trim());
    } else {
      nextParams.delete("search");
    }
    setSearchParams(nextParams, { replace: true });

    fetchContacts(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const isNarrowing = debouncedSearch.trim() !== "";
  const clearSearch = () => setSearch("");

  const columns: astryx.TableColumn<IContactRecord>[] = [
    {
      key: "firstName",
      header: "First name",
      width: astryx.proportional(1),
    },
    {
      key: "lastName",
      header: "Last name",
      width: astryx.proportional(1),
    },
    {
      key: "email",
      header: "Email",
      width: astryx.proportional(1),
    },
    {
      key: "status",
      header: "Status",
      width: astryx.pixel(120),
      renderCell: (contact) => (
        <astryx.Badge
          variant={contact.status === "active" ? "success" : "neutral"}
          label={contact.status === "active" ? "Active" : "Inactive"}
        />
      ),
    },
    {
      key: "actions",
      header: "Actions",
      width: astryx.pixel(100),
      renderCell: (contact) => (
        <astryx.Button
          label="Edit"
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/admin/contacts/${contact.id}/edit`)}
        />
      ),
    },
  ];

  return (
    <astryx.Theme theme={neutralTheme} mode={mode}>
      <PageContainer>
        <PageTopbar breadcrumbs={breadcrumbs} />

        <ContentContainer loading={false}>
          <PageHeader
            title="Contacts"
            description="Manage contacts in your organization."
            action={
              <astryx.Button
                label="New contact"
                variant="primary"
                icon={<astryx.Icon icon={Plus} />}
                onClick={() => navigate("/admin/contacts/new")}
              />
            }
          />

          <astryx.VStack gap={6}>
            <astryx.HStack gap={3} align="center" wrap="wrap">
              <astryx.StackItem size="fill">
                <astryx.TextInput
                  label="Search contacts"
                  isLabelHidden
                  startIcon={<astryx.Icon icon={Search} />}
                  placeholder="Search by name or email…"
                  hasClear
                  width="100%"
                  value={search}
                  onChange={(value: string) => setSearch(value)}
                />
              </astryx.StackItem>
            </astryx.HStack>

            <DataTable
              loading={loading}
              data={contacts}
              columns={columns}
              idKey="id"
              emptyState={
                isNarrowing
                  ? {
                      icon: <astryx.Icon icon={Contact} />,
                      headline: "No contacts match",
                      subtext: "Try a different search.",
                      action: (
                        <astryx.Button
                          label="Clear search"
                          onClick={clearSearch}
                        />
                      ),
                    }
                  : {
                      icon: <astryx.Icon icon={Contact} />,
                      headline: "No contacts yet",
                      subtext: "Add your first contact to get started.",
                      action: (
                        <astryx.Button
                          label="Add contact"
                          variant="primary"
                          onClick={() => navigate("/admin/contacts/new")}
                        />
                      ),
                    }
              }
              pagination={{
                page: pagination.page,
                totalPages: pagination.pages ?? 1,
                onChange: (page: number) => fetchContacts(page),
              }}
            />
          </astryx.VStack>
        </ContentContainer>
      </PageContainer>
    </astryx.Theme>
  );
};

export default ContactsIndex;
